'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function KazAyagiBotoksuPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/kaz-ayagi-botoksu-nedir';
  const pageTitle = 'Kaz Ayağı Botoksu Nasıl Yapılır? – Göz Çevresi Kırışıklıklarına Etkili Çözüm';
  const pageDescription = 'Göz çevresi kırışıklıklarına etkili çözüm: Kaz ayağı botoksu nasıl yapılır, etkisi ne kadar sürer, fiyatları nedir?';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Kaz Ayağı Botoksu Nasıl Yapılır?', href: '/tr/blog/kaz-ayagi-botoksu-nedir' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Kaz ayağı botoksu uygulaması"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Estetik Tedaviler
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Kaz Ayağı Botoksu Nasıl Yapılır? – Göz Çevresi Kırışıklıklarına Etkili Çözüm
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>17 Ocak 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>6 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Gülerken ya da mimik yaparken göz çevresinde oluşan çizgiler, zamanla kalıcı hale gelir ve yaşlı bir görünüm yaratır. Estetik kaygı duyan birçok kişi için kaz ayağı botoksu etkili ve hızlı bir çözümdür. Bu yazıda, göz çevresi botoksunun nasıl yapıldığını, etkilerini ve dikkat edilmesi gerekenleri ele aldık.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaz Ayağı Botoksu Nedir?
              </h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Kaz ayağı botoksu, göz kenarındaki mimik kaslarına düşük dozda botulinum toksini enjeksiyonu yapılarak kırışıklıkların yumuşatılması işlemidir. Uygulamanın amacı, gülme esnasında ortaya çıkan çizgileri azaltmak ve göz çevresine daha genç bir ifade kazandırmaktır.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaz Ayağı Botoksu Nasıl Yapılır?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Kaz ayağı botoksu işlemi genellikle 5–10 dakika sürer ve aşağıdaki adımlarla gerçekleştirilir:
              </p>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Ön Değerlendirme</h3>
                  <p className="text-gray-700">Hekim, hastanın göz çevresindeki mimik hareketlerini inceler ve enjeksiyon noktalarını belirler.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Enjeksiyon</h3>
                  <p className="text-gray-700">İnce uçlu bir iğneyle her iki göz kenarına 2–3 noktadan küçük dozlarda botoks uygulanır. Genellikle ağrısızdır, hafif batma hissi olabilir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Sonrası</h3>
                  <p className="text-gray-700">Hasta işlemin hemen ardından günlük yaşantısına dönebilir. Tam etkiler ortalama 5–7 gün içinde görülür.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Etkisi Ne Kadar Sürer?
              </h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Kaz ayağı botoksunun etkisi genellikle <strong>4 ila 6 ay</strong> arasında sürer. İlk uygulamalarda bu süre daha kısa olabilir; ancak düzenli uygulamalarda daha kalıcı sonuçlar alınır.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaz Ayağı Botoksu Fiyatları 2026
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                2026 yılı itibariyle göz çevresi botoksu için ortalama fiyatlar şu şekildedir:
              </p>
              
              {/* Price Table */}
              <div className="overflow-x-auto mb-8">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Uygulama Bölgesi
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Fiyat Aralığı (₺)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900">Kaz Ayağı Botoksu</td>
                      <td className="px-6 py-4 text-sm text-gray-900">2.000 – 3.500</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">Göz Çevresi + Kaş Arası</td>
                      <td className="px-6 py-4 text-sm text-gray-900">4.000 – 5.500</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-lg mb-8">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      💡 <strong>İpucu:</strong> Kombine uygulamalar (kaş arası, alın ve göz çevresi birlikte) daha ekonomik olabilir.
                    </p>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kimler İçin Uygun / Uygun Değil?
              </h2>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-green-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-green-700 mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Kimler İçin Uygundur?
                  </h3>
                  <ul className="space-y-2 text-green-800">
                    <li>• Gülerken göz çevresi çizgileri belirgin olanlar</li>
                    <li>• 30 yaş üzeri kırışıklık eğilimli cilt yapısına sahip kişiler</li>
                    <li>• Daha canlı ve genç bakışlara sahip olmak isteyenler</li>
                  </ul>
                </div>

                <div className="bg-red-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-red-700 mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Kimler İçin Uygun Değildir?
                  </h3>
                  <ul className="space-y-2 text-red-800">
                    <li>• Hamile ve emzirenler</li>
                    <li>• Sinir-kas hastalığı olanlar</li>
                    <li>• Göz çevresinde enfeksiyonu bulunanlar</li>
                    <li>• Botulinum toksinine alerjisi olan bireyler</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Uygulama Öncesi ve Sonrası Dikkat Edilmesi Gerekenler
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-blue-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-blue-700 mb-4">Öncesinde:</h3>
                  <ul className="space-y-2 text-blue-800">
                    <li>• Kan sulandırıcı ilaçlar hekime danışılarak bırakılmalıdır</li>
                    <li>• Uygulama günü makyaj yapılmamalı</li>
                  </ul>
                </div>

                <div className="bg-purple-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-purple-700 mb-4">Sonrasında:</h3>
                  <ul className="space-y-2 text-purple-800">
                    <li>• Göz çevresine masaj yapılmamalı</li>
                    <li>• İlk 4 saat baş aşağı pozisyonlardan kaçınılmalı</li>
                    <li>• 24 saat boyunca ağır spor ve sauna gibi aktivitelerden uzak durulmalı</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sıkça Sorulan Sorular
              </h2>
              
              <div className="space-y-4 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    1. Göz çevresindeki doğal ifade kaybolur mu?
                  </h3>
                  <p className="text-gray-700">
                    Hayır. Doğru dozla yapılan uygulamalarda doğal mimikler korunur.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    2. Morarma olur mu?
                  </h3>
                  <p className="text-gray-700">
                    Nadir görülür. Genellikle geçicidir ve 1–2 gün içinde kaybolur.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    3. Göz kapağında düşme olur mu?
                  </h3>
                  <p className="text-gray-700">
                    Uzman ellerde yapılan enjeksiyonlarda bu risk çok düşüktür.
                  </p>
                </div>
              </div>

              {/* CTA Section */}
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white text-center mb-8">
                <h3 className="text-2xl font-bold mb-4">
                  Kaz Ayağı Botoksu Hakkında Daha Fazla Bilgi Almak İster misiniz?
                </h3>
                <p className="text-lg mb-6 opacity-90">
                  Op. Dr. Barış Çin ile ücretsiz konsültasyon için hemen iletişime geçin.
                </p>
                <a
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Merhaba, kaz ayağı botoksu hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-8 rounded-full transition-colors duration-200"
                >
                  <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.690z"/>
                  </svg>
                  WhatsApp ile Randevu Al
                </a>
              </div>

              {/* References */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Kaynakça</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>• American Academy of Ophthalmology – <a href="https://www.aao.org" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://www.aao.org</a></li>
                    <li>• Estetik ve Plastik Cerrahi Derneği – Göz çevresi uygulamaları kılavuzu</li>
                    <li>• 2026 İstanbul – Ankara özel muayenehane fiyat araştırması</li>
                    <li>• Türk Dermatoloji Derneği Bilgilendirme Notları</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}